import React, { useState } from 'react';
import {
  Box,
  CardContent,
  Typography,
  Button,
  Chip,
  Alert,
  CircularProgress,
  Divider,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Container,
  Paper,
  Stack,
} from '@mui/material';
import {
  CheckCircle as CheckIcon,
  Star as StarIcon,
  Restore as RestoreIcon,
  Close as CloseIcon,
  WorkspacePremium as PremiumIcon,
} from '@mui/icons-material';
import { useSubscription } from '../hooks/useSubscription';
import { useAnalytics } from '../hooks/useAnalytics';
import { useAppTheme } from '../App';
import { REVENUECAT_CONFIG } from '../config/revenuecat.config';
import AuthLayout from '../components/AuthLayout';

interface SubscriptionPlan {
  id: string | null; // null for free plan
  name: string;
  price: string;
  period: string;
  features: string[];
  limitations?: string[];
  popular?: boolean;
  current?: boolean;
}

const Subscription: React.FC = () => {
  const { getThemeStyles, isDarkMode } = useAppTheme();
  const theme = getThemeStyles();
  const {
    isActive,
    isPro,
    loading,
    error,
    purchase,
    restore,
  } = useSubscription();
  const { trackSubscriptionEvent, trackPageView } = useAnalytics();

  // Debug logging for subscription page
  console.log('🔍 Subscription Page Debug:');
  console.log('🔍 isActive:', isActive);
  console.log('🔍 isPro:', isPro);
  console.log('🔍 loading:', loading);

  const [selectedPlan, setSelectedPlan] = useState<string | null>(null);
  const [purchaseError, setPurchaseError] = useState<string | null>(null);

  // Track page view on component mount
  React.useEffect(() => {
    trackPageView('subscription_page');
    trackSubscriptionEvent('view_plans');
  }, [trackPageView, trackSubscriptionEvent]);

  const subscriptionPlans: SubscriptionPlan[] = [
    {
      id: null, // Free plan
      name: 'Free Plan',
      price: '$0',
      period: 'forever',
      current: !isPro,
      features: [
        'Basic visa guidance',
        '3 eligibility checks per day',
        'Document upload: 1 CV, 1 LinkedIn, 3 additional docs',
        'Community support',
      ],
      limitations: [
        'No assistant chat access',
        'Personal statement generation requires payment',
        'Limited daily eligibility checks',
        'Basic document storage limit',
      ],
    },
    {
      id: REVENUECAT_CONFIG.products.pro_weekly,
      name: 'Pro Plan',
      price: '$10',
      period: 'per week',
      popular: true,
      current: isPro,
      features: [
        'Advanced Assistant chat access',
        'Unlimited eligibility checks per day',
        'Document upload: 1 CV, 1 LinkedIn, 10 additional docs',
        'Priority support',
        'Advanced AI-powered guidance',
        'Export to PDF/DOC',
        'Early access to new features',
        'Dedicated customer success manager',
      ],
    },
  ];

  // Debug the plan current status
  console.log('🔍 Plan Current Status:');
  console.log('🔍 Free plan current:', !isPro);
  console.log('🔍 Pro plan current:', isPro);

  const handlePurchase = async (planId: string) => {
    try {
      setPurchaseError(null);
      setSelectedPlan(planId);
      
      // Track subscription click event
      const planName = planId === REVENUECAT_CONFIG.products.pro_weekly ? 'pro_weekly' : planId;
      trackSubscriptionEvent('start_trial', planName);
      
      await purchase(planId);
      
      // Track successful subscription
      trackSubscriptionEvent('subscribe', planName);
    } catch (err) {
      setPurchaseError(err instanceof Error ? err.message : 'Purchase failed');
    } finally {
      setSelectedPlan(null);
    }
  };

  const handleRestore = async () => {
    try {
      setPurchaseError(null);
      await restore();
    } catch (err) {
      setPurchaseError(err instanceof Error ? err.message : 'Restore failed');
    }
  };

  return (
    <AuthLayout>
      <Box
        sx={{
          backgroundColor: theme.background,
          minHeight: '100vh',
        }}
      >
        <Container maxWidth="lg">
          <Box sx={{ py: { xs: 3, sm: 6 } }}>
            {/* Header */}
            <Box textAlign="center" mb={{ xs: 4, sm: 6 }}>
              <Typography
                variant="h3"
                component="h1"
                sx={{
                  color: theme.textPrimary,
                  fontWeight: 700,
                  mb: 2,
                  fontSize: { xs: '2rem', sm: '2.5rem', md: '3rem' },
                  letterSpacing: '-0.02em',
                }}
              >
                Choose Your Plan
              </Typography>
              <Typography
                variant="h6"
                sx={{
                  color: theme.textSecondary,
                  maxWidth: 600,
                  mx: 'auto',
                  fontSize: { xs: '1rem', sm: '1.125rem' },
                  fontWeight: 400,
                  lineHeight: 1.6,
                }}
              >
                Unlock the full potential of TheVisaAI with our Pro plan
              </Typography>
            </Box>

            {/* Current Status Alert */}
            {isActive && (
              <Alert
                severity="success"
                icon={<PremiumIcon />}
                sx={{
                  mb: 4,
                  backgroundColor: isDarkMode 
                    ? 'rgba(76, 175, 80, 0.15)' 
                    : 'rgba(76, 175, 80, 0.08)',
                  color: isDarkMode ? '#81c784' : '#2e7d32',
                  border: `1px solid ${isDarkMode ? 'rgba(76, 175, 80, 0.3)' : 'rgba(76, 175, 80, 0.2)'}`,
                  borderRadius: 2,
                  '& .MuiAlert-icon': {
                    color: isDarkMode ? '#81c784' : '#2e7d32',
                  },
                }}
              >
                <Typography variant="subtitle1" fontWeight={600}>
                  {isPro ? 'You are currently on the Pro plan!' : 'You have an active subscription!'}
                </Typography>
              </Alert>
            )}

            {/* Error Alert */}
            {(error || purchaseError) && (
              <Alert
                severity="error"
                sx={{ 
                  mb: 4,
                  borderRadius: 2,
                  backgroundColor: isDarkMode 
                    ? 'rgba(244, 67, 54, 0.15)' 
                    : 'rgba(244, 67, 54, 0.08)',
                  color: isDarkMode ? '#ef5350' : '#d32f2f',
                  border: `1px solid ${isDarkMode ? 'rgba(244, 67, 54, 0.3)' : 'rgba(244, 67, 54, 0.2)'}`,
                }}
                onClose={() => setPurchaseError(null)}
              >
                {error || purchaseError}
              </Alert>
            )}

            {/* Subscription Plans */}
            <Box 
              sx={{
                display: 'flex',
                flexDirection: { xs: 'column', md: 'row' },
                gap: { xs: 2, sm: 3, md: 4 },
                justifyContent: 'center',
                alignItems: 'stretch',
                mb: 6,
              }}
            >
              {subscriptionPlans.map((plan) => (
                <Box key={plan.name} sx={{ flex: { xs: 'none', md: 1 }, maxWidth: { xs: '100%', md: 450 } }}>
                  <Paper
                    elevation={plan.popular ? 8 : 2}
                    sx={{
                      position: 'relative',
                      height: '100%',
                      backgroundColor: theme.cardBackground,
                      borderRadius: 3,
                      border: plan.popular 
                        ? `2px solid #FE6A2E` 
                        : plan.current 
                          ? `2px solid #4caf50` 
                          : `1px solid ${theme.borderColor}`,
                      transform: plan.popular ? { xs: 'none', md: 'scale(1.05)' } : 'none',
                      transition: 'all 0.3s ease-in-out',
                      overflow: 'visible',
                      '&:hover': {
                        transform: plan.popular 
                          ? { xs: 'translateY(-4px)', md: 'scale(1.05) translateY(-4px)' }
                          : 'translateY(-4px)',
                        boxShadow: isDarkMode 
                          ? '0 12px 40px rgba(0, 0, 0, 0.4)' 
                          : '0 12px 40px rgba(0, 0, 0, 0.15)',
                      },
                    }}
                  >
                    {/* Popular Badge */}
                    {plan.popular && (
                      <Chip
                        label="Recommended"
                        icon={<StarIcon sx={{ fontSize: '1rem' }} />}
                        sx={{
                          position: 'absolute',
                          top: -12,
                          left: '50%',
                          transform: 'translateX(-50%)',
                          backgroundColor: '#FE6A2E',
                          color: 'white',
                          fontWeight: 700,
                          fontSize: '0.75rem',
                          height: 28,
                          zIndex: 1,
                          boxShadow: '0 4px 8px rgba(254, 106, 46, 0.3)',
                        }}
                      />
                    )}

                    {/* Current Plan Badge */}
                    {plan.current && (
                      <Chip
                        label="Current Plan"
                        sx={{
                          position: 'absolute',
                          top: 16,
                          right: 16,
                          backgroundColor: '#4caf50',
                          color: 'white',
                          fontWeight: 600,
                          fontSize: '0.75rem',
                          height: 24,
                        }}
                      />
                    )}

                    <CardContent sx={{ p: { xs: 3, sm: 4 }, height: '100%', display: 'flex', flexDirection: 'column' }}>
                      {/* Plan Header */}
                      <Box textAlign="center" mb={3}>
                        <Typography
                          variant="h5"
                          component="h3"
                          sx={{ 
                            color: theme.textPrimary, 
                            fontWeight: 700, 
                            mb: 2,
                            fontSize: { xs: '1.25rem', sm: '1.5rem' },
                          }}
                        >
                          {plan.name}
                        </Typography>
                        
                        <Stack direction="row" alignItems="baseline" justifyContent="center" spacing={0.5} mb={1}>
                          <Typography
                            variant="h2"
                            component="span"
                            sx={{ 
                              color: plan.id ? '#FE6A2E' : theme.textPrimary, 
                              fontWeight: 800,
                              fontSize: { xs: '2.5rem', sm: '3rem' },
                              lineHeight: 1,
                            }}
                          >
                            {plan.price}
                          </Typography>
                        </Stack>
                        
                        <Typography 
                          variant="body2" 
                          sx={{ 
                            color: theme.textSecondary,
                            fontSize: '0.875rem',
                            fontWeight: 500,
                          }}
                        >
                          {plan.id ? 'per week • 3-day free trial' : plan.period}
                        </Typography>

                        {plan.id && (
                          <Chip
                            label="3-Day Free Trial"
                            size="small"
                            sx={{
                              mt: 1,
                              backgroundColor: isDarkMode 
                                ? 'rgba(76, 175, 80, 0.2)' 
                                : 'rgba(76, 175, 80, 0.1)',
                              color: '#4caf50',
                              fontWeight: 600,
                              fontSize: '0.7rem',
                              height: 24,
                            }}
                          />
                        )}
                      </Box>

                      <Divider sx={{ mb: 3, backgroundColor: theme.borderColor, opacity: 0.6 }} />

                      {/* Features */}
                      <Box sx={{ flex: 1 }}>
                        <List sx={{ p: 0 }}>
                          {plan.features.map((feature, featureIndex) => (
                            <ListItem key={featureIndex} sx={{ px: 0, py: 0.75 }}>
                              <ListItemIcon sx={{ minWidth: 32 }}>
                                <CheckIcon sx={{ color: '#4caf50', fontSize: 18 }} />
                              </ListItemIcon>
                              <ListItemText
                                primary={feature}
                                primaryTypographyProps={{
                                  color: theme.textPrimary,
                                  fontSize: '0.875rem',
                                  fontWeight: 500,
                                  lineHeight: 1.4,
                                }}
                              />
                            </ListItem>
                          ))}
                          
                          {/* Limitations for Free plan */}
                          {plan.limitations?.map((limitation, limitIndex) => (
                            <ListItem key={`limitation-${limitIndex}`} sx={{ px: 0, py: 0.75 }}>
                              <ListItemIcon sx={{ minWidth: 32 }}>
                                <CloseIcon sx={{ color: '#f44336', fontSize: 18 }} />
                              </ListItemIcon>
                              <ListItemText
                                primary={limitation}
                                primaryTypographyProps={{
                                  color: theme.textSecondary,
                                  fontSize: '0.875rem',
                                  fontWeight: 400,
                                  lineHeight: 1.4,
                                }}
                              />
                            </ListItem>
                          ))}
                        </List>
                      </Box>

                      {/* Action Button */}
                      <Box sx={{ mt: 3 }}>
                        {plan.id ? (
                          <Button
                            fullWidth
                            variant="contained"
                            size="large"
                            disabled={loading || selectedPlan === plan.id || plan.current}
                            onClick={() => handlePurchase(plan.id!)}
                            sx={{
                              backgroundColor: '#FE6A2E',
                              color: 'white',
                              fontWeight: 700,
                              py: { xs: 1.5, sm: 2 },
                              borderRadius: 2,
                              textTransform: 'none',
                              fontSize: { xs: '1rem', sm: '1.125rem' },
                              boxShadow: plan.current 
                                ? 'none' 
                                : '0 4px 16px rgba(254, 106, 46, 0.3)',
                              '&:hover': {
                                backgroundColor: '#E5511A',
                                boxShadow: plan.current 
                                  ? 'none' 
                                  : '0 6px 20px rgba(254, 106, 46, 0.4)',
                                transform: plan.current ? 'none' : 'translateY(-1px)',
                              },
                              '&:disabled': {
                                background: plan.current ? '#4caf50' : theme.borderColor,
                                color: plan.current ? 'white' : theme.textSecondary,
                                boxShadow: 'none',
                              },
                            }}
                          >
                            {selectedPlan === plan.id ? (
                              <CircularProgress size={24} color="inherit" />
                            ) : plan.current ? (
                              'Current Plan'
                            ) : (
                              'Start 3-Day Free Trial'
                            )}
                          </Button>
                        ) : (
                          <Button
                            fullWidth
                            variant="outlined"
                            size="large"
                            disabled={plan.current}
                            sx={{
                              borderColor: plan.current ? '#4caf50' : theme.borderColor,
                              color: plan.current ? '#4caf50' : theme.textSecondary,
                              fontWeight: 600,
                              py: { xs: 1.5, sm: 2 },
                              borderRadius: 2,
                              textTransform: 'none',
                              fontSize: { xs: '1rem', sm: '1.125rem' },
                              borderWidth: plan.current ? 2 : 1,
                              '&:hover': {
                                borderColor: plan.current ? '#4caf50' : theme.textPrimary,
                                backgroundColor: 'transparent',
                              },
                            }}
                          >
                            {plan.current ? 'Current Plan' : 'Free Forever'}
                          </Button>
                        )}
                      </Box>
                    </CardContent>
                  </Paper>
                </Box>
              ))}
            </Box>

            {/* Restore Purchases */}
            <Box textAlign="center">
              <Button
                variant="text"
                startIcon={<RestoreIcon />}
                onClick={handleRestore}
                disabled={loading}
                sx={{
                  color: theme.textSecondary,
                  textTransform: 'none',
                  fontWeight: 500,
                  px: 3,
                  py: 1,
                  borderRadius: 2,
                  '&:hover': {
                    color: '#FE6A2E',
                    backgroundColor: isDarkMode 
                      ? 'rgba(254, 106, 46, 0.08)' 
                      : 'rgba(254, 106, 46, 0.04)',
                  },
                }}
              >
                Restore Purchases
              </Button>
            </Box>
          </Box>
        </Container>
      </Box>
    </AuthLayout>
  );
};

export default Subscription; 